import { FunctionComponent, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTimes, faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import styles from './AutomationWizardHeader.module.scss';

const STATUS_LABELS: Record<string, string> = {
    draft: 'Draft',
    active: 'Active',
    paused: 'Inactive',
    archived: 'Archived'
};

interface AutomationWizardHeaderProps {
    title: string;
    onClose: () => void;
    onSave?: () => void;
    saveBlockers?: string[];
    status?: string;
    onTitleChange?: (title: string) => void;
    isSaving?: boolean;
    actionLabel?: string;
}

const AutomationWizardHeader: FunctionComponent<AutomationWizardHeaderProps> = ({
    title,
    onClose,
    onSave,
    saveBlockers = [],
    status,
    onTitleChange,
    isSaving,
    actionLabel = 'Save'
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(title);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editing) {
            setDraft(title);
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing, title]);

    const handleConfirm = () => {
        const trimmed = draft.trim();
        if (trimmed) onTitleChange?.(trimmed);
        setEditing(false);
    };

    const handleCancel = () => {
        setEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleConfirm();
        if (e.key === 'Escape') handleCancel();
    };

    const disabled = isSaving || !onSave;
    const blocked = disabled && !isSaving && saveBlockers.length > 0;

    const saveButton = (
        <button
            className={styles.saveButton}
            onClick={onSave}
            disabled={disabled}
            style={disabled ? { pointerEvents: 'none' } : undefined}
        >
            {actionLabel}
        </button>
    );

    return (
        <div className={styles.container}>
            <div className={styles.mainRow}>
                <div className={styles.titleArea}>
                    {editing ? (
                        <>
                            <input
                                ref={inputRef}
                                className={styles.titleInput}
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <button
                                type="button"
                                className={styles.inlineAction}
                                onClick={handleConfirm}
                                title="Confirm"
                            >
                                <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button
                                type="button"
                                className={`${styles.inlineAction} ${styles.inlineActionCancel}`}
                                onClick={handleCancel}
                                title="Cancel"
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </>
                    ) : (
                        <>
                            <span className={styles.title}>{title}</span>
                            <FontAwesomeIcon
                                icon={faPenToSquare}
                                className={styles.editIcon}
                                onClick={() => setEditing(true)}
                            />
                            <span className={clsx(styles.draftBadge, styles[status ?? 'draft'])}>
                                {STATUS_LABELS[status ?? 'draft'] ?? 'Draft'}
                            </span>
                        </>
                    )}
                </div>
                <div className={styles.actions}>
                    {blocked ? (
                        <Tooltip
                            placement="bottom"
                            className={styles.saveTooltip}
                            content={
                                <div className={styles.saveTooltipBody}>
                                    <strong>{`${actionLabel} is disabled because:`}</strong>
                                    <ul>
                                        {saveBlockers.map(reason => (
                                            <li key={reason}>{reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            }
                        >
                            {saveButton}
                        </Tooltip>
                    ) : (
                        saveButton
                    )}
                    <FontAwesomeIcon
                        icon={faTimes}
                        className={styles.closeIcon}
                        onClick={onClose}
                    />
                </div>
            </div>
        </div>
    );
};

export default AutomationWizardHeader;
